import { ZIO } from '@mxt/zio'
import { AuthService, POApi } from '@pulseops/common'
import { pipe } from 'fp-ts/function'
import * as t from 'io-ts'

const nullableString = t.union([t.string, t.null])

const currentUserName = pipe(
  AuthService.userInfo,
  ZIO.map((userInfo) => userInfo.name || userInfo.email || '')
)

export type PolicyDispatchingOfficesResponse = {
  body: {
    items: Array<{
      officeCode: string
      officeBranchCode: string | null
      officeBankCode: string | null
    }>
    systemDate: string
  }
}

export type StockInSearchItem = {
  agentNumber: string
  branch: string
  contractNumber: string
  policyIssueDate: string
  remark: string | null
  status: string
  stockInDate?: string | null
  newStockInDate?: string | null
  deliveryDate: string | null
}

export type StockInPayload = Omit<StockInSearchItem, 'status'> & {
  status: string
  newOffice?: string | null
}

export type StockInSearchResponse = {
  body: {
    items: StockInSearchItem[]
    page: {
      first: boolean
      last: boolean
      page: number
      size: number
      totalElements: number
      totalPages: number
    }
  }
  responseStatus: {
    code: number
    message: string
  }
}

export type StockInSearchNoDataResponse = {
  responseStatus: {
    code: number
    message: string
  }
}

export type QuickPolicyInformation = {
  ackLetterInfo?: {
    customerSignedDate: string | null
    letterEmployee: string | null
    letterEntryDate: string | null
    letterOffice: string | null
    letterType: string | null
    overdueDays: number | null
    remark: string | null
    scannedOnFilenet: boolean | null
  } | null
  agentInfo?: {
    agentName: string | null
    agentNumber: string | null
    agentSalesBranch: string | null
    agentSalesUnitCode: string | null
    area: string | null
    branch: string | null
    dateAppointed: string | null
    dateTerminate: string | null
    gaBp: string | null
    saleZone: string | null
    status: string | null
    supportingAgentName: string | null
    type: string | null
  } | null
  branch: string | null
  contractNumber: string | null
  contractOwnerName: string | null
  crossReferenceNumber: string | null
  distributionInfo?: { deliveryDate: string | null; deliveryOffice: string | null; receiver: string | null } | null
  firstIssueDate: string | null
  issueDates: string[] | null
  lasRecordedDate: string | null
  officeCodes: string[] | null
  status: string | null
  statusDescription: string | null
  stockInfo?: {
    returnDate: string | null
    returnOffice: string | null
    stockInDate: string | null
    stockInEmployee: string | null
    stockInOffice: string | null
  } | null
}

export type QuickPolicyInformationResponse =
  | { body: QuickPolicyInformation; responseStatus: { code: number; message: string } }
  | { responseStatus: { code: number; message: string } }

export type OldQuickPolicyInformation = {
  ackLetters: Array<{
    collectionChannel: string | null
    confirmLetterId: number
    customerSignedDate: string | null
    letterEmployee: string | null
    letterEntryDate: string | null
    letterOffice: string | null
    letterType: string | null
    overdueDays: number | null
    remark: string | null
  }>
  agentInformation: {
    agentName: string | null
    agentNumber: string | null
    branchCode: string | null
    dateAppointed: string | null
    dateTerminate: string | null
    gaBp: string | null
    officeCode: string | null
    regionCode: string | null
    status: string | null
    supportingAgentName: string | null
    supportingAgentNumber: string | null
    type: string | null
    unitCode: string | null
    zoneCode: string | null
  } | null
  contractInformation: {
    contractNumber: string | null
    contractOwnerName: string | null
    firstIssueDate: string | null
    issueDates: string[] | null
    lasRecordedDate: string | null
    officeCode: string | null
    proposalNumber: string | null
    servicingBranch: string | null
    statusCode: string | null
    statusDescription: string | null
  } | null
  distributionInformation: {
    deliveryDate: string | null
    deliveryOffice: string | null
    receiver: string | null
  } | null
  stockInformation: {
    holdDate: string | null
    holdEmployee: string | null
    holdOffice: string | null
    holdReason: string | null
    returnDate: string | null
    returnOffice: string | null
    stockInDate: string | null
    stockInEmployee: string | null
    stockInOffice: string | null
  } | null
}

export type OldQuickPolicyInformationResponse =
  | { body: OldQuickPolicyInformation; responseStatus: { code: number; message: string } }
  | { responseStatus: { code: number; message: string } }

export type StockOutAgentPolicy = {
  contractNumber: string
  firstIssueDate: string
  issueDate: string
  stockInDate: string | null
  letterEntryDate: string | null
  branch: string
  status: string
  deliveryDate: string | null
  receiver: string | null
  note: string | null
}

export type StockOutAgentSearchBody = {
  agent: {
    agentCode: string
    agentName: string
    status: string
    gaCode: string
    branch: string
    type: string
    salesUnitCode: string
    salesBranch: string
    dateAppointed: string
    dateTerminated: string | null
    area: string
    zone: string
  }
  policies: StockOutAgentPolicy[]
  totalElements: number
  totalPages: number
  page: number
  size: number
}

export type StockOutAgentSearchResponse =
  | StockOutAgentSearchBody
  | {
      body: StockOutAgentSearchBody
      responseStatus?: {
        code: number
        message: string
      }
    }

export type StockOutAgentGroupContract = {
  contractNumber: string
  firstIssueDate: string
  issueDate: string
  office: string
  agentNumber: string
  agentName: string
  deliveryDate: string
  stockInDate: string | null
  receiver: string
  status: string
  remark: string
}

export type StockOutAgentGroupDeliveryPayload = {
  contractNumber: string
  agentNumber: string
  status: string
  receiver: string
  remark: string
}

export type StockOutAgentDeliveryPayload = {
  contractNumber: string
  agentCode: string
  receiver: string
  remark: string
}

export type StockOutAgentCancelPayload = {
  contractNumber: string
  agentCode: string
}

export type StockOutAgentChangeDatePayload = {
  contractNumber: string
  agentCode: string
  newDeliveryDate: string
}

export type StockOutPostOfficeDeliveryPayload = {
  contractNumber: string
  invoiceCode: string
  deliveryDate: string
  remark: string
}

export type StockOutPostOfficeCancelPayload = {
  contractNumber: string
  remark: string
}

export type StockOutPostOfficeChangeDatePayload = {
  contractNumber: string
  newDeliveryDate: string
  remark: string
}

export type StockOutAgentGroupChangeDatePayload = StockOutAgentGroupDeliveryPayload & {
  newDeliveryDate: string
}

export type StockOutAgentGroupSearchBody = {
  informationGroup: {
    leaderCode: string
    leaderName: string
    status: string
    type: string
    salesBranch: string
    area: string
    office: string
    zone: string
  }
  informationReceiver: {
    leaderCode: string
    leaderName: string
    leaderBranchCode: string
    leaderBranchName: string
  }
  listContract: StockOutAgentGroupContract[]
  page: {
    first: boolean
    last: boolean
    page: number
    size: number
    totalElements: number
    totalPages: number
  }
}

export type StockOutAgentGroupSearchResponse = {
  body: StockOutAgentGroupSearchBody
  responseStatus: {
    code: number
    message: string
  }
}

export type AuthorizationLetterAgentInformation = {
  agentName: string
  agentNumber: string
  agentSalesBranch: string
  agentSalesUnitCode: string
  area: string
  branch: string
  dateAppointed: string
  dateTerminate: string | null
  gaBp: string
  saleZone: string
  status: string
  type: string
}

export type AuthorizationLetterAgentResponse = {
  body: {
    agentGroups: string[]
    agentInformation: AuthorizationLetterAgentInformation
    approvers: string[]
  }
  responseStatus: {
    code: number
    message: string
  }
}

export type AgentInfoGroupLeader = {
  address: string | null
  branch: string | null
  dateAppointed: string | null
  dateTerminate: string | null
  gaBp: string | null
  leaderCode: string | null
  leaderName: string | null
  mum: string | null
  phoneNumber1: string | null
  phoneNumber2: string | null
  rdm: string | null
  rdo: string | null
  status: string | null
  type: string | null
}

export type AgentInfoItem = {
  address: string | null
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  area: string | null
  branch: string | null
  dateAppointed: string | null
  dateTerminate: string | null
  gaBp: string | null
  groupLeader: AgentInfoGroupLeader | null
  saleZone: string | null
  status: string | null
  type: string | null
  phoneNumber1: string | null
  phoneNumber2: string | null
}

export type AgentInfoResponse =
  | {
      body: AgentInfoItem
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type AuthorizationLetterItem = {
  agentGroup: string
  agentNumber: string
  allowDeactivate: boolean
  approver: string
  authorizationLetterId: string
  authorizedPerson: string
  createDate: string
  effectiveFromDate: string
  effectiveToDate: string
  id: string
  officeCode: string
  status: string
}

export type AuthorizationLettersResponse = {
  body: {
    letters: AuthorizationLetterItem[]
    page: number
    size: number
    totalElements: number
    totalPages: number
  }
  responseStatus: {
    code: number
    message: string
  }
}

export type AuthorizationLetterCreatePayload = {
  authorizedPerson: string
  approver: string
  effectiveFromDate: string | null
  effectiveToDate: string | null
  agentNumber: string
  agentGroup: string[]
  owner: string
  authorizer: string
}

export type AuthorizationLetterDeactivationPayload =
  | {
      authorizationLetterId: string
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type StockOutPostOfficeContract = {
  contractNumber: string
  firstIssueDate: string | null
  issueDate: string | null
  branch: string | null
  agentNumber: string
  agentName: string
  status: string
  invoiceCode: string | null
  postOfficeConfirmDate: string | null
  customerSignDate: string | null
  deliveryDate: string | null
  remark: string | null
}

export type StockOutPostOfficeSearchBody = {
  information: {
    agentNumber: string
    agentName: string
    latestIssueDate: string | null
    stockInDate: string | null
  } | null
  contracts: StockOutPostOfficeContract[]
  totalElements: number
  totalPages: number
  page: number
  size: number
}

export type AuthorizedPerson = {
  agentNumber: string
  approver: string
  authorizationLetterId: string
  authorizedPerson: string
  effectiveFromDate: string
  effectiveToDate: string
}

export type AuthorizationRow = {
  id: string
  authorizationAgent: string
  authorizedPerson: string
  effectiveDate: string
  expiryDate: string
  approver: string
}

export type AreaManagerRow = {
  fullName: string
  joinDate: string
  office: string
  id: string
  type: string
}

export type AuthorizedPersonResponse =
  | {
      body: {
        items: AuthorizedPerson[]
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type AckManagementSearchItem = {
  agentName: string | null
  agentNumber: string | null
  contactStatus: string | null
  contractNumber: string | null
  correctContactAddress: string | null
  customerSignedDate: string | null
  deliveryDate: string | null
  imageFile: string | null
  letterEntryDate: string | null
  letterType: string | null
  mainDoc: string | null
  policyIssueDate: string | null
  postOfficeReturnDate: string | null
  recipientName: string | null
  remark: string | null
  resendCount: number | null
  shareAgentName: string | null
  shareAgentNumber: string | null
  status: string | null
  subDoc: string | null
  existed: boolean | null
}

export type AckManagementSearchResponse = {
  body?: AckManagementSearchItem
  responseStatus?: {
    code: number
    message: string
  }
}

export type QuickSearchAckInformation = {
  agentName: string | null
  agentNumber: string | null
  contractNumber: string | null
  customerSignedDate: string | null
  letterEntryDate: string | null
  letterType: string | null
  officeCode: string | null
  policyIssueDate: string | null
  remark: string | null
}

export type QuickSearchAckInformationResponse = {
  body: QuickSearchAckInformation
  responseStatus: {
    code: number
    message: string
  }
}

export type AckNotReturnSearchItem = {
  billingFrequency: string | null
  contractNumber: string | null
  deliveryDate: string | null
  office: string | null
  overdueDays: number | null
  ownerName: string | null
  policyIssueDate: string | null
  receiver: string | null
  referenceNumber: string | null
  statusCode: string | null
  stockInDate: string | null
}

export type AckNotReturnSearchResponse =
  | {
      body: {
        agentName: string
        items: AckNotReturnSearchItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type CheckLogItem = {
  action: string | null
  actionTime: string | null
  contractNumber: string | null
  logNo: string | null
  office: string | null
  remark: string | null
  status: string | null
  step: string | null
  userName: string | null
}

export type CheckLogSearchResponse =
  | {
      body: {
        items: CheckLogItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type QuickSearchAuthorizationLetter = {
  approver: string | null
  authorizationLetterId: string | null
  authorizedContractOf: string[] | null
  authorizedPerson: string | null
  authorizerCode: string[] | null
  authorizerName: string | null
  createDate: string | null
  effectiveFromDate: string | null
  effectiveToDate: string | null
  owner: string | null
}

export type QuickSearchAuthorizationLetterResponse =
  | {
      body: QuickSearchAuthorizationLetter
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type PoliciesByAgentSearchItem = {
  billingFrequency: string | null
  contractNumber: string | null
  contractOwner: string | null
  crossReferenceNumber: string | null
  issueDate: string | null
  notes: string | null
  office: string | null
  status: string | null
  stockInDate: string | null
}

export type PoliciesByAgentSearchResponse =
  | {
      body: {
        agentNumber: string | null
        agentName: string | null
        page: number
        size: number
        totalElements: number
        totalPages: number
        policies: PoliciesByAgentSearchItem[]
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type ConfirmLettersReportItem = {
  agentNumber: string | null
  contractNumber: string | null
  customerSignedDate: string | null
  letterEntryDate: string | null
  officeContract: string | null
  letterType: string | null
  officeCode: string | null
  policyIssueDate: string | null
}

export type ConfirmLettersReportResponse =
  | {
      body: {
        items: ConfirmLettersReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type DeductionReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentType: string | null
  branch: string | null
  contractNumber: string | null
  customerSignedDate: string | null
  delayedReceiptDays: number | null
  deliveryDate: string | null
  invoiceCode: string | null
  leaderCode: string | null
  officeCode: string | null
  policyIssueDate: string | null
  stockInDate: string | null
}

export type DeductionReportResponse =
  | {
      body: {
        items: DeductionReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type DeliveredContractsReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  area: string | null
  branch: string | null
  officeContract: string | null
  contractNumber: string | null
  deliveryDate: string | null
  policyIssueDate: string | null
  receiver: string | null
  remark: string | null
  saleZone: string | null
  status: string | null
  stockInDate: string | null
}

export type DeliveredContractsReportResponse =
  | {
      body: {
        items: DeliveredContractsReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type IssuedReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  area: string | null
  branch: string | null
  contractNumber: string | null
  officeCode: string | null
  policyIssueDate: string | null
  proposalNumber: string | null
  saleZone: string | null
  waitingDays: number | null
}

export type IssuedReportResponse =
  | {
      body: {
        items: IssuedReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type NonReturnLetterReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  area: string | null
  branch: string | null
  contractNumber: string | null
  deliveryDate: string | null
  officeCode: string | null
  overdueDays: number | null
  policyIssueDate: string | null
  saleZone: string | null
  stockInDate: string | null
}

export type NonReturnLetterReportResponse =
  | {
      body: {
        items: NonReturnLetterReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type ContractsEmsCustomerReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentType: string | null
  branch: string | null
  contractNumber: string | null
  customerSignedDate: string | null
  delayedReceiptDays: number | null
  deliveryDate: string | null
  invoiceCode: string | null
  leaderCode: string | null
  policyIssueDate: string | null
  stockInDate: string | null
  receiver: string | null
}

export type ContractsEmsCustomerReportResponse =
  | {
      body: {
        items: ContractsEmsCustomerReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type ContractsForSigningReportItem = {
  agentName: string | null
  agentNumber: string | null
  contractNumber: string | null
  customerNumber: string | null
  policyIssueDate: string | null
  productCode: string | null
  sharedAgentNumber: string | null
  unitCode: string | null
}

export type ContractsForSigningReportResponse =
  | {
      body: {
        items: ContractsForSigningReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
        printDate: string
        totalContracts: number
        totalReceivedToday: number
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type ContractsInStockReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  officeContract: string | null
  area: string | null
  branch: string | null
  contractNumber: string | null
  overdueDaysFromPolicyIssueDate: number | null
  overdueDaysFromStockInDate: number | null
  policyIssueDate: string | null
  status: string | null
  stockInDate: string | null
}

export type ContractsInStockReportResponse =
  | {
      body: {
        items: ContractsInStockReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type ReceivedContractsReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  officeContract: string | null
  area: string | null
  branch: string | null
  contractNumber: string | null
  officeCode: string | null
  policyIssueDate: string | null
  saleZone: string | null
  status: string | null
  stockInDate: string | null
}

export type ReceivedContractsReportResponse =
  | {
      body: {
        items: ReceivedContractsReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type UndeliveredContractsReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  area: string | null
  branch: string | null
  contractNumber: string | null
  officeCode: string | null
  overdueDays: number | null
  policyIssueDate: string | null
  saleZone: string | null
  status: string | null
  stockInDate: string | null
}

export type UndeliveredContractsReportResponse =
  | {
      body: {
        items: UndeliveredContractsReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type NonReturnPostsReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentType: string | null
  contractNumber: string | null
  delayedReceiptDays: number | null
  deliveryDate: string | null
  invoiceCode: string | null
  leaderCode: string | null
  officeCode: string | null
  policyIssueDate: string | null
  postalDeliveryDays: number | null
  stockInDate: string | null
}

export type NonReturnPostsReportResponse =
  | {
      body: {
        items: NonReturnPostsReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type UndeliveredContractsByPostReportItem = {
  agentNumber: string | null
  agentType: string | null
  branch: string | null
  contactStatus: string | null
  contractNumber: string | null
  delayedReceiptDays: number | null
  deliveryDate: string | null
  invoiceCode: string | null
  leaderCode: string | null
  officeCode: string | null
  policyIssueDate: string | null
  postOfficeReturnDate: string | null
  remark: string | null
  resendCount: number | null
  stockInDate: string | null
}

export type UndeliveredContractsByPostReportResponse =
  | {
      body: {
        items: UndeliveredContractsByPostReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type AgentListReportItem = {
  agentName: string | null
  agentNumber: string | null
  agentSalesBranch: string | null
  agentSalesUnitCode: string | null
  agentType: string | null
  leaderCode: string | null
  mumUnitCode: string | null
  officeCode: string | null
}

export type AgentListReportResponse =
  | {
      body: {
        items: AgentListReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type AdminReportItem = {
  agentNumber: string | null
  ackFileNet: boolean | null
  branchCode: string | null
  contractNumber: string | null
  customerSignedDate: string | null
  deliveryDate: string | null
  lasAckRecordedDate: string | null
  letterEntryDate: string | null
  officeCode: string | null
  policyIssueDate: string | null
  productName: string | null
  stockInDate: string | null
}

export type AdminReportResponse =
  | {
      body: {
        items: AdminReportItem[]
        page: {
          first: boolean
          last: boolean
          page: number
          size: number
          totalElements: number
          totalPages: number
        }
      }
      responseStatus: {
        code: number
        message: string
      }
    }
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type RdoItem = {
  area: string | null
  department: string | null
  fullName: string | null
  gaBp: string | null
  group: string | null
  inactive: boolean
  joinDate: string | null
  office: string | null
  rdoCode: string | null
  signatureUrl: string | null
  type: string | null
}

export type RdoSearchResponseHasValue = {
  body: {
    items: RdoItem[]
    page: {
      first: boolean
      last: boolean
      page: number
      size: number
      totalElements: number
      totalPages: number
    }
  }
  responseStatus: {
    code: number
    message: string
  }
}

export type RdoSearchResponse =
  | RdoSearchResponseHasValue
  | {
      responseStatus: {
        code: number
        message: string
      }
    }

export type AckManagementSavePayload = PolicyDispatchingService.AckManagementSavePayload

export namespace PolicyDispatchingService {
  export type AckManagementSavePayload = {
    contractNumber: string
    officeCode: string | null
    deliveryDate: string | null
    customerSignedDate: string | null
    letterType: string | null
    remark: string | null
    contactStatus: string | null
    postOfficeReturnDate: string | null
    recipientName: string | null
    correctContactAddress: string | null
  }

  export type StockInSearchPayload = {
    issueDate: string
    contractNumber?: string
    status?: string | null
    remark?: string
    stockInDate?: string | null
    agentNumber?: string
    officeCode: string | null
    page: number
    isDownload?: boolean
  }

  export type UploadStockInPayload = {
    contractNumber: string
    agentNumber: string
    policyIssueDate: string
    officeCode: string
  }

  export type UploadChangeOfficePayload = {
    contractNumber: string
    newOffice: string
  }

  export type StockOutAgentSearchPayload = {
    agentCode: string
    officeCode: string | null
    status: string
    page: number
  }

  export type StockOutAgentGroupSearchPayload = {
    officeCode: string
    agentGroup: string
    status: string
    page: string
  }

  export type StockOutPostOfficeSearchPayload = {
    contractNumber: string
    officeCode: string
    status: string
    page: number
  }

  export type AckNotReturnSearchPayload = {
    agentNumber: string
    page: number
  }

  export type CheckLogSearchPayload = {
    contractNumber: string
    fromDate?: string
    toDate?: string
    userName?: string
    action?: string
    page: number
    size?: number
  }

  export type PoliciesByAgentSearchPayload = {
    agentNumber: string
    page: number
  }

  export type ConfirmLettersReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type DeductionReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type DeliveredContractsReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type IssuedReportSearchPayload = {
    officeCode: string | null
    page: number
  }

  export type NonReturnLetterReportSearchPayload = {
    officeCode: string | null
    page: number
  }

  export type ContractsEmsCustomerReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type ContractsForSigningReportSearchPayload = {
    officeCode: string | null
    page: number
  }

  export type ContractsInStockReportSearchPayload = {
    officeCode: string | null
    page: number
  }

  export type ReceivedContractsReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type UndeliveredContractsReportSearchPayload = {
    officeCode: string | null
    page: number
  }

  export type NonReturnPostsReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type UndeliveredContractsByPostReportSearchPayload = {
    fromDate: string
    toDate: string
    officeCode: string | null
    page: number
  }

  export type RdoSearchPayload = {
    rdoCode?: string
    fullName?: string
    page?: number
    nonFileNet?: string
  }

  export type CreateRdoApproverPayload = {
    rdoCode: string
    fullName: string
    joinDate: string
    office: string
    gaBp: string
    area: string
    group: string
    department: string
    type: string
    inactive: boolean
  }

  export type UpdateRdoPayload = CreateRdoApproverPayload

  export type AgentListReportSearchPayload = {
    officeCode: string | null
    agentType: string | null
    page: number
  }

  export type AdminReportSearchPayload = {
    fromDate: string
    toDate: string
    page: number
  }

  export type AuthorizationLettersSearchPayload = {
    authorizationLetterId?: string
    agentNumber?: string
    agentGroup?: string
    createdFromDate?: string | null
    createdToDate?: string | null
    officeCode: string
    page: number
  }

  const stockOutAgentPolicyCodec = t.type({
    contractNumber: t.string,
    firstIssueDate: t.string,
    issueDate: t.string,
    stockInDate: nullableString,
    letterEntryDate: nullableString,
    branch: t.string,
    status: t.string,
    deliveryDate: nullableString,
    receiver: nullableString,
    note: nullableString
  })

  const stockOutAgentSearchBodyCodec = t.type({
    agent: t.type({
      agentCode: t.string,
      agentName: t.string,
      status: t.string,
      gaCode: t.string,
      branch: t.string,
      type: t.string,
      salesUnitCode: t.string,
      salesBranch: t.string,
      dateAppointed: t.string,
      dateTerminated: nullableString,
      area: t.string,
      zone: t.string
    }),
    policies: t.array(stockOutAgentPolicyCodec),
    totalElements: t.number,
    totalPages: t.number,
    page: t.number,
    size: t.number
  })

  export const getOffices = pipe(
    POApi.get('policy-dispatching-api/offices')(
      t.type({
        body: t.type({
          items: t.array(
            t.type({
              officeCode: t.string,
              officeBranchCode: nullableString,
              officeBankCode: nullableString
            })
          ),
          systemDate: t.string
        })
      })
    ),
    ZIO.map((response) => response)
  )

  const searchStockInCodec = t.union([
    t.type({
      body: t.type({
        items: t.array(
          t.type({
            agentNumber: t.string,
            branch: t.string,
            contractNumber: t.string,
            policyIssueDate: t.string,
            deliveryDate: nullableString,
            remark: nullableString,
            status: t.string,
            stockInDate: nullableString
          })
        ),
        page: t.type({
          first: t.boolean,
          last: t.boolean,
          page: t.number,
          size: t.number,
          totalElements: t.number,
          totalPages: t.number
        })
      }),
      responseStatus: t.type({
        code: t.number,
        message: t.string
      })
    }),
    t.type({
      responseStatus: t.type({
        code: t.number,
        message: t.string
      })
    })
  ])

  export const searchStockIn = (payload: StockInSearchPayload) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.get('policy-dispatching-api/stock-in/search', { params: { ...payload, userName } })(searchStockInCodec)
      )
    )

  export const stockIn = (officeCode: string | null, payload: StockInPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-in', { params: { officeCode, userName } })(t.unknown)(payload)
      )
    )

  export const giveBack = (officeCode: string | null, payload: StockInPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-in/give-back', { params: { officeCode, userName } })(t.unknown)(
          payload
        )
      )
    )

  export const changeDate = (officeCode: string | null, payload: StockInPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.put('policy-dispatching-api/stock-in/change-date', { params: { officeCode, userName } })(t.unknown)(
          payload
        )
      )
    )

  export const changeOffice = (officeCode: string | null, payload: StockInPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.put('policy-dispatching-api/stock-in/change-office', { params: { officeCode, userName } })(t.unknown)(
          payload
        )
      )
    )

  export const uploadStockIn = (payload: UploadStockInPayload[]) =>
    POApi.post('policy-dispatching-api/upload/stock-in')(t.unknown)(payload)

  export const uploadChangeOffice = (payload: UploadChangeOfficePayload[]) =>
    POApi.put('policy-dispatching-api/upload/change-office')(t.unknown)(payload)

  export const searchStockOutAgent = (payload: StockOutAgentSearchPayload) =>
    POApi.get('policy-dispatching-api/stock-out/agent/search', { params: payload })(
      t.union([
        stockOutAgentSearchBodyCodec,
        t.type({
          body: stockOutAgentSearchBodyCodec,
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchStockOutAgentGroup = (payload: StockOutAgentGroupSearchPayload) =>
    POApi.get('policy-dispatching-api/stock-out/group-agent/search', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            informationGroup: t.type({
              leaderCode: t.string,
              leaderName: t.string,
              status: t.string,
              type: t.string,
              salesBranch: t.string,
              area: t.string,
              office: t.string,
              zone: t.string
            }),
            informationReceiver: t.type({
              leaderCode: t.string,
              leaderName: t.string,
              leaderBranchCode: t.string,
              leaderBranchName: t.string
            }),
            listContract: t.array(
              t.type({
                contractNumber: t.string,
                firstIssueDate: t.string,
                issueDate: t.string,
                office: t.string,
                agentNumber: t.string,
                agentName: t.string,
                deliveryDate: t.string,
                receiver: t.string,
                stockInDate: t.string,
                status: t.string,
                remark: t.string
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchStockOutPostOffice = (payload: StockOutPostOfficeSearchPayload) =>
    POApi.get('policy-dispatching-api/stock-out/post-office/search', { params: payload })(
      t.type({
        information: t.union([
          t.type({
            agentNumber: t.string,
            agentName: t.string,
            latestIssueDate: nullableString,
            stockInDate: nullableString
          }),
          t.null
        ]),
        contracts: t.array(
          t.type({
            contractNumber: t.string,
            firstIssueDate: t.string,
            issueDate: t.string,
            branch: t.string,
            agentNumber: t.string,
            agentName: t.string,
            status: t.string,
            invoiceCode: nullableString,
            postOfficeConfirmDate: nullableString,
            customerSignDate: nullableString,
            deliveryDate: nullableString,
            remark: nullableString
          })
        ),
        totalElements: t.number,
        totalPages: t.number,
        page: t.number,
        size: t.number
      })
    )

  export const getAuthorizedPersons = (agentGroup: string) =>
    POApi.get('policy-dispatching-api/stock-out/group-agent/authorized-person', { params: { agentGroup } })(
      t.type({
        body: t.type({
          items: t.array(
            t.type({
              agentNumber: t.string,
              approver: t.string,
              authorizationLetterId: t.string,
              authorizedPerson: t.string,
              effectiveFromDate: t.string,
              effectiveToDate: t.string
            })
          )
        }),
        responseStatus: t.type({
          code: t.number,
          message: t.string
        })
      })
    )

  export const getAuthorizedPersonsByAgent = (agentNumber: string) =>
    POApi.get('policy-dispatching-api/stock-out/agent/authorized-person', { params: { agentNumber } })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentNumber: t.string,
                approver: t.string,
                authorizationLetterId: t.string,
                authorizedPerson: t.string,
                effectiveFromDate: t.string,
                effectiveToDate: t.string
              })
            )
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const getAgentInfo = (agentNumber: string) =>
    POApi.get('policy-dispatching-api/quick-search/agent-info', { params: { agentNumber } })(
      t.union([
        t.type({
          body: t.type({
            address: nullableString,
            agentName: nullableString,
            agentNumber: nullableString,
            agentSalesBranch: nullableString,
            agentSalesUnitCode: nullableString,
            area: nullableString,
            branch: nullableString,
            dateAppointed: nullableString,
            dateTerminate: nullableString,
            gaBp: nullableString,
            groupLeader: t.union([
              t.type({
                address: nullableString,
                branch: nullableString,
                dateAppointed: nullableString,
                dateTerminate: nullableString,
                gaBp: nullableString,
                leaderCode: nullableString,
                leaderName: nullableString,
                mum: nullableString,
                phoneNumber1: nullableString,
                phoneNumber2: nullableString,
                rdm: nullableString,
                rdo: nullableString,
                status: nullableString,
                type: nullableString
              }),
              t.null
            ]),
            saleZone: nullableString,
            status: nullableString,
            type: nullableString,
            phoneNumber1: nullableString,
            phoneNumber2: nullableString
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchAuthorizationLetterAgent = (agentNumber: string) =>
    POApi.get('policy-dispatching-api/authorization-letters/agents', { params: { agentNumber } })(
      t.union([
        t.type({
          body: t.type({
            agentGroups: t.array(t.string),
            agentInformation: t.type({
              agentName: t.string,
              agentNumber: t.string,
              agentSalesBranch: t.string,
              agentSalesUnitCode: t.string,
              area: t.string,
              branch: t.string,
              dateAppointed: t.string,
              dateTerminate: nullableString,
              gaBp: t.string,
              saleZone: t.string,
              status: t.string,
              type: t.string
            }),
            approvers: t.array(t.string)
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchAuthorizationLetters = (payload: AuthorizationLettersSearchPayload) =>
    POApi.get('policy-dispatching-api/authorization-letters', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            letters: t.array(
              t.type({
                agentGroup: t.string,
                agentNumber: t.string,
                allowDeactivate: t.boolean,
                approver: t.string,
                authorizationLetterId: t.string,
                authorizedPerson: t.string,
                createDate: t.string,
                effectiveFromDate: t.string,
                effectiveToDate: t.string,
                id: t.string,
                officeCode: t.string,
                status: t.string
              })
            ),
            page: t.number,
            size: t.number,
            totalElements: t.number,
            totalPages: t.number
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const createAuthorizationLetter = (officeCode: string | null, payload: AuthorizationLetterCreatePayload) =>
    POApi.post('policy-dispatching-api/authorization-letters', { params: { officeCode } })(t.unknown)(payload)

  export const deactivateAuthorizationLetters = (payload: AuthorizationLetterDeactivationPayload[]) =>
    POApi.put('policy-dispatching-api/authorization-letters/deactivation')(t.unknown)(payload)

  export const deliveryStockOutAgentGroup = (payload: StockOutAgentGroupDeliveryPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-out/group-agent/delivery-contract', { params: { userName } })(
          t.unknown
        )(payload)
      )
    )

  export const deliveryStockOutPostOffice = (payload: StockOutPostOfficeDeliveryPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-out/post-office/deliver', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const cancelStockOutAgentGroup = (payload: StockOutAgentGroupDeliveryPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-out/group-agent/cancel', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const cancelStockOutPostOffice = (payload: StockOutPostOfficeCancelPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-out/post-office/cancel', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const changeDateStockOutPostOffice = (payload: StockOutPostOfficeChangeDatePayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.put('policy-dispatching-api/stock-out/post-office/change-date', { params: { userName } })(t.unknown)(
          payload
        )
      )
    )

  export const changeDateStockOutAgentGroup = (payload: StockOutAgentGroupChangeDatePayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.put('policy-dispatching-api/stock-out/group-agent/change-date', { params: { userName } })(t.unknown)(
          payload
        )
      )
    )

  export const deliverStockOutAgent = (payload: StockOutAgentDeliveryPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-out/agent/deliver', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const cancelStockOutAgent = (payload: StockOutAgentCancelPayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/stock-out/agent/cancel', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const changeDateStockOutAgent = (payload: StockOutAgentChangeDatePayload[]) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.put('policy-dispatching-api/stock-out/agent/change-date', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const searchAckManagement = (contractNumber: string) =>
    POApi.get('policy-dispatching-api/ack-management/search', { params: { contractNumber } })(
      t.union([
        t.type({
          body: t.type({
            agentName: nullableString,
            agentNumber: nullableString,
            contactStatus: nullableString,
            contractNumber: nullableString,
            correctContactAddress: nullableString,
            customerSignedDate: nullableString,
            deliveryDate: nullableString,
            imageFile: nullableString,
            letterEntryDate: nullableString,
            letterType: nullableString,
            mainDoc: nullableString,
            policyIssueDate: nullableString,
            postOfficeReturnDate: nullableString,
            recipientName: nullableString,
            remark: nullableString,
            resendCount: t.union([t.number, t.null]),
            shareAgentName: nullableString,
            shareAgentNumber: nullableString,
            status: nullableString,
            subDoc: nullableString,
            existed: t.boolean
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchCheckLogs = (payload: CheckLogSearchPayload) =>
    POApi.get('policy-dispatching-api/check-logs/search', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                action: nullableString,
                actionTime: nullableString,
                contractNumber: nullableString,
                logNo: nullableString,
                office: nullableString,
                remark: nullableString,
                status: nullableString,
                step: nullableString,
                userName: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchAckNotReturn = (payload: AckNotReturnSearchPayload) =>
    POApi.get('policy-dispatching-api/quick-search/ack-not-return', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            agentName: nullableString,
            items: t.array(
              t.type({
                billingFrequency: nullableString,
                contractNumber: nullableString,
                deliveryDate: nullableString,
                office: nullableString,
                overdueDays: t.union([t.number, t.null]),
                ownerName: nullableString,
                policyIssueDate: nullableString,
                receiver: nullableString,
                referenceNumber: nullableString,
                statusCode: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchConfirmLettersReport = (payload: ConfirmLettersReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/confirm-letters', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentNumber: nullableString,
                contractNumber: nullableString,
                customerSignedDate: nullableString,
                letterEntryDate: nullableString,
                letterType: nullableString,
                officeContract: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchDeductionReport = (payload: DeductionReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/deduction-report', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentType: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                customerSignedDate: nullableString,
                delayedReceiptDays: t.union([t.number, t.null]),
                deliveryDate: nullableString,
                invoiceCode: nullableString,
                leaderCode: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchDeliveredContractsReport = (payload: DeliveredContractsReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/delivered-contracts', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                officeContract: nullableString,
                deliveryDate: nullableString,
                policyIssueDate: nullableString,
                receiver: nullableString,
                remark: nullableString,
                saleZone: nullableString,
                status: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchIssuedReport = (payload: IssuedReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/issued-report', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString,
                proposalNumber: nullableString,
                saleZone: nullableString,
                waitingDays: t.union([t.number, t.null])
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchNonReturnLetterReport = (payload: NonReturnLetterReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/non-return-letter', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                deliveryDate: nullableString,
                officeCode: nullableString,
                overdueDays: t.union([t.number, t.null]),
                policyIssueDate: nullableString,
                saleZone: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchContractsEmsCustomerReport = (payload: ContractsEmsCustomerReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/contracts-ems-customer', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentType: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                customerSignedDate: nullableString,
                delayedReceiptDays: t.union([t.number, t.null]),
                deliveryDate: nullableString,
                invoiceCode: nullableString,
                leaderCode: nullableString,
                policyIssueDate: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchContractsForSigningReport = (payload: ContractsForSigningReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/contracts-for-signing', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                contractNumber: nullableString,
                customerNumber: nullableString,
                policyIssueDate: nullableString,
                productCode: nullableString,
                sharedAgentNumber: nullableString,
                unitCode: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            }),
            printDate: t.string,
            totalContracts: t.number,
            totalReceivedToday: t.number
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchContractsInStockReport = (payload: ContractsInStockReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/contracts-in-stock', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                officeContract: nullableString,
                overdueDaysFromPolicyIssueDate: t.union([t.number, t.null]),
                overdueDaysFromStockInDate: t.union([t.number, t.null]),
                policyIssueDate: nullableString,
                status: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchReceivedContractsReport = (payload: ReceivedContractsReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/received-contracts', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString,
                officeContract: nullableString,
                saleZone: nullableString,
                status: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchUndeliveredContractsReport = (payload: UndeliveredContractsReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/undelivered-contracts', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                contractNumber: nullableString,
                officeCode: nullableString,
                overdueDays: t.union([t.number, t.null]),
                policyIssueDate: nullableString,
                saleZone: nullableString,
                status: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchNonReturnPostsReport = (payload: NonReturnPostsReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/non-return-posts', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentType: nullableString,
                contractNumber: nullableString,
                delayedReceiptDays: t.union([t.number, t.null]),
                deliveryDate: nullableString,
                invoiceCode: nullableString,
                leaderCode: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString,
                postalDeliveryDays: t.union([t.number, t.null]),
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchUndeliveredContractsByPostReport = (payload: UndeliveredContractsByPostReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/undelivered-contracts/by-post', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentNumber: nullableString,
                agentType: nullableString,
                branch: nullableString,
                contactStatus: nullableString,
                contractNumber: nullableString,
                delayedReceiptDays: t.union([t.number, t.null]),
                deliveryDate: nullableString,
                invoiceCode: nullableString,
                leaderCode: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString,
                postOfficeReturnDate: nullableString,
                remark: nullableString,
                resendCount: t.union([t.number, t.null]),
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const getNewestRdoCode = POApi.get('policy-dispatching-api/rdo/newest-rdo')(
    t.type({
      body: t.type({
        newestRdoCode: t.string
      }),
      responseStatus: t.type({
        code: t.number,
        message: t.string
      })
    })
  )

  export const createRdoApprover = (payload: CreateRdoApproverPayload) =>
    POApi.post('policy-dispatching-api/rdo/create-approver')(
      t.type({
        responseStatus: t.type({
          code: t.number,
          message: t.string
        })
      })
    )(payload)

  export const updateRdo = (payload: UpdateRdoPayload) =>
    POApi.put('policy-dispatching-api/rdo/update')(
      t.type({
        responseStatus: t.type({
          code: t.number,
          message: t.string
        })
      })
    )(payload)

  export const searchRdo = (payload?: RdoSearchPayload) =>
    POApi.get('policy-dispatching-api/rdo/search', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                area: nullableString,
                department: nullableString,
                fullName: nullableString,
                gaBp: nullableString,
                group: nullableString,
                inactive: t.boolean,
                joinDate: nullableString,
                office: nullableString,
                rdoCode: nullableString,
                signatureUrl: nullableString,
                type: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const deactivateRdo = (rdoCodes: string[]) =>
    POApi.put('policy-dispatching-api/rdo/deactive')(
      t.type({
        responseStatus: t.type({
          code: t.number,
          message: t.string
        })
      })
    )(rdoCodes)

  export const searchAgentListReport = (payload: AgentListReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/agent-list', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                agentType: nullableString,
                leaderCode: nullableString,
                mumUnitCode: nullableString,
                officeCode: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchAdminReport = (payload: AdminReportSearchPayload) =>
    POApi.get('policy-dispatching-api/report/admin-report', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            items: t.array(
              t.type({
                agentNumber: nullableString,
                ackFileNet: t.union([t.boolean, t.null]),
                branchCode: nullableString,
                contractNumber: nullableString,
                customerSignedDate: nullableString,
                deliveryDate: nullableString,
                lasAckRecordedDate: nullableString,
                letterEntryDate: nullableString,
                officeCode: nullableString,
                policyIssueDate: nullableString,
                productName: nullableString,
                stockInDate: nullableString
              })
            ),
            page: t.type({
              first: t.boolean,
              last: t.boolean,
              page: t.number,
              size: t.number,
              totalElements: t.number,
              totalPages: t.number
            })
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchQuickPoliciesByAgent = (payload: PoliciesByAgentSearchPayload) =>
    POApi.get('policy-dispatching-api/quick-search/policies-by-agent', { params: payload })(
      t.union([
        t.type({
          body: t.type({
            agentNumber: nullableString,
            agentName: nullableString,
            page: t.number,
            size: t.number,
            totalElements: t.number,
            totalPages: t.number,
            policies: t.array(
              t.type({
                billingFrequency: nullableString,
                contractNumber: nullableString,
                contractOwner: nullableString,
                crossReferenceNumber: nullableString,
                issueDate: nullableString,
                notes: nullableString,
                office: nullableString,
                status: nullableString,
                stockInDate: nullableString
              })
            )
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchQuickAuthorizationLetter = (authorizationLetterId: string) =>
    POApi.get('policy-dispatching-api/quick-search/authorization-letter', { params: { authorizationLetterId } })(
      t.union([
        t.type({
          body: t.type({
            approver: nullableString,
            authorizationLetterId: nullableString,
            authorizedContractOf: t.union([t.array(t.string), t.null]),
            authorizedPerson: nullableString,
            authorizerCode: t.union([t.array(t.string), t.null]),
            authorizerName: nullableString,
            createDate: nullableString,
            effectiveFromDate: nullableString,
            effectiveToDate: nullableString,
            owner: nullableString
          }),
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        }),
        t.type({
          responseStatus: t.type({
            code: t.number,
            message: t.string
          })
        })
      ])
    )

  export const searchQuickAckInformation = (contractNumber: string) =>
    POApi.get('policy-dispatching-api/quick-search/ack-information', { params: { contractNumber } })(
      t.type({
        body: t.type({
          agentName: nullableString,
          agentNumber: nullableString,
          contractNumber: nullableString,
          customerSignedDate: nullableString,
          letterEntryDate: nullableString,
          letterType: nullableString,
          officeCode: nullableString,
          policyIssueDate: nullableString,
          remark: nullableString
        }),
        responseStatus: t.type({
          code: t.number,
          message: t.string
        })
      })
    )

  export const searchQuickPolicyInformation = (contractNumber: string) =>
    POApi.get('policy-dispatching-api/quick-search/policy-info', { params: { contractNumber } })(
      t.union([
        t.type({
          body: t.type({
            ackLetterInfo: t.union([
              t.type({
                customerSignedDate: nullableString,
                letterEmployee: nullableString,
                letterEntryDate: nullableString,
                letterOffice: nullableString,
                letterType: nullableString,
                overdueDays: t.union([t.number, t.null]),
                remark: nullableString,
                scannedOnFilenet: t.union([t.boolean, t.null])
              }),
              t.null
            ]),
            agentInfo: t.union([
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                agentSalesBranch: nullableString,
                agentSalesUnitCode: nullableString,
                area: nullableString,
                branch: nullableString,
                dateAppointed: nullableString,
                dateTerminate: nullableString,
                gaBp: nullableString,
                saleZone: nullableString,
                status: nullableString,
                supportingAgentName: nullableString,
                type: nullableString
              }),
              t.null
            ]),
            branch: nullableString,
            contractNumber: nullableString,
            contractOwnerName: nullableString,
            crossReferenceNumber: nullableString,
            distributionInfo: t.union([
              t.type({ deliveryDate: nullableString, deliveryOffice: nullableString, receiver: nullableString }),
              t.null
            ]),
            firstIssueDate: nullableString,
            issueDates: t.union([t.array(t.string), t.null]),
            lasRecordedDate: nullableString,
            officeCodes: t.union([t.array(t.string), t.null]),
            status: nullableString,
            statusDescription: nullableString,
            stockInfo: t.union([
              t.type({
                returnDate: nullableString,
                returnOffice: nullableString,
                stockInDate: nullableString,
                stockInEmployee: nullableString,
                stockInOffice: nullableString
              }),
              t.null
            ])
          }),
          responseStatus: t.type({ code: t.number, message: t.string })
        }),
        t.type({ responseStatus: t.type({ code: t.number, message: t.string }) })
      ])
    )

  export const searchOldQuickPolicyInformation = (contractNumber: string) =>
    POApi.get('policy-dispatching-api/quick-search/policy-info/old-pd', { params: { contractNumber } })(
      t.union([
        t.type({
          body: t.type({
            ackLetters: t.array(
              t.type({
                collectionChannel: nullableString,
                confirmLetterId: t.number,
                customerSignedDate: nullableString,
                letterEmployee: nullableString,
                letterEntryDate: nullableString,
                letterOffice: nullableString,
                letterType: nullableString,
                overdueDays: t.union([t.number, t.null]),
                remark: nullableString
              })
            ),
            agentInformation: t.union([
              t.type({
                agentName: nullableString,
                agentNumber: nullableString,
                branchCode: nullableString,
                dateAppointed: nullableString,
                dateTerminate: nullableString,
                gaBp: nullableString,
                officeCode: nullableString,
                regionCode: nullableString,
                status: nullableString,
                supportingAgentName: nullableString,
                supportingAgentNumber: nullableString,
                type: nullableString,
                unitCode: nullableString,
                zoneCode: nullableString
              }),
              t.null
            ]),
            contractInformation: t.union([
              t.type({
                contractNumber: nullableString,
                contractOwnerName: nullableString,
                firstIssueDate: nullableString,
                issueDates: t.union([t.array(t.string), t.null]),
                lasRecordedDate: nullableString,
                officeCode: nullableString,
                proposalNumber: nullableString,
                servicingBranch: nullableString,
                statusCode: nullableString,
                statusDescription: nullableString
              }),
              t.null
            ]),
            distributionInformation: t.union([
              t.type({ deliveryDate: nullableString, deliveryOffice: nullableString, receiver: nullableString }),
              t.null
            ]),
            stockInformation: t.union([
              t.type({
                holdDate: nullableString,
                holdEmployee: nullableString,
                holdOffice: nullableString,
                holdReason: nullableString,
                returnDate: nullableString,
                returnOffice: nullableString,
                stockInDate: nullableString,
                stockInEmployee: nullableString,
                stockInOffice: nullableString
              }),
              t.null
            ])
          }),
          responseStatus: t.type({ code: t.number, message: t.string })
        }),
        t.type({ responseStatus: t.type({ code: t.number, message: t.string }) })
      ])
    )

  export const createAckManagement = (payload: AckManagementSavePayload) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.post('policy-dispatching-api/ack-management', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const updateAckManagement = (payload: AckManagementSavePayload) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.put('policy-dispatching-api/ack-management', { params: { userName } })(t.unknown)(payload)
      )
    )

  export const deleteAckManagement = (contractNumber: string) =>
    pipe(
      currentUserName,
      ZIO.flatMap((userName) =>
        POApi.delError({ params: { contractNumber, userName } })('policy-dispatching-api/ack-management')(t.unknown)
      )
    )
}
